import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { registerParent } from '../../../redux/parentRelated/parentHandle';
import { getAllSclasses } from '../../../redux/sclassRelated/sclassHandle';
import { useToast } from '../../../context/ToastContext';
import { underControl } from '../../../redux/parentRelated/parentSlice';
import { 
    CircularProgress, Box, Typography, Button, TextField, 
    Grid, Paper, Autocomplete, Chip, IconButton, Container, Divider, Avatar
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PersonIcon from '@mui/icons-material/Person'; 
import LocationOnIcon from '@mui/icons-material/LocationOn';
 import SchoolIcon from '@mui/icons-material/School';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
 import axios from 'axios';
import styled, { keyframes } from 'styled-components';

const AddParent = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { status, response, error } = useSelector(state => state.parent);
    const { currentUser } = useSelector(state => state.user);
    const { sclassesList } = useSelector((state) => state.sclass);

    // Father information
    const [fatherName, setFatherName] = useState('');
    const [fatherOccupation, setFatherOccupation] = useState('');
    const [fatherPhone, setFatherPhone] = useState('');
    const [fatherEmail, setFatherEmail] = useState('');

    // Mother information
    const [motherName, setMotherName] = useState('');
    const [motherOccupation, setMotherOccupation] = useState('');
    const [motherPhone, setMotherPhone] = useState('');
    const [motherEmail, setMotherEmail] = useState('');

    // Guardian information
    const [guardianName, setGuardianName] = useState('');
    const [guardianRelation, setGuardianRelation] = useState('');
    const [guardianPhone, setGuardianPhone] = useState('');
    const [guardianEmail, setGuardianEmail] = useState('');
    const [guardianAddress, setGuardianAddress] = useState('');

    // General information
    const [address, setAddress] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [photo, setPhoto] = useState(null);
    const [photoPreview, setPhotoPreview] = useState('');

    // Students linked to parent
    const [selectedSclass, setSelectedSclass] = useState(null);
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [availableStudents, setAvailableStudents] = useState([]);
    const [studentsLoading, setStudentsLoading] = useState(false);

    const { showToast } = useToast();
    const [loader, setLoader] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const school = currentUser._id;
    const role = "Parent";

    // Fetch classes on mount
    useEffect(() => {
        dispatch(getAllSclasses(school, "Sclass"));
    }, [school, dispatch]);

    // Fetch students when class changes
    useEffect(() => {
        if (selectedSclass) {
            const fetchClassStudents = async () => {
                setStudentsLoading(true);
                try {
                    const result = await axios.get(`${process.env.REACT_APP_BASE_URL}/Sclass/Students/${selectedSclass._id}`);
                    if (result.data && Array.isArray(result.data)) {
                        // Filter students who don't have parents assigned yet
                        const studentsWithoutParents = result.data.filter(student => !student.parent);
                        setAvailableStudents(studentsWithoutParents);
                    } else {
                        setAvailableStudents([]);
                    }
                } catch (err) {
                    console.error("Error fetching class students:", err);
                    setAvailableStudents([]);
                }
                setStudentsLoading(false);
            };
            fetchClassStudents();
        } else {
            setAvailableStudents([]);
            setSelectedStudents([]);
        }
    }, [selectedSclass]);

    useEffect(() => {
        dispatch(underControl());
    }, [dispatch]);

    const handlePhotoChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setPhoto(file);
            const reader = new FileReader();
            reader.onloadend = () => setPhotoPreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const removePhoto = () => {
        setPhoto(null);
        setPhotoPreview(null);
    };

    const submitHandler = (event) => {
        event.preventDefault();
        
        if (!fatherName || !password) {
            showToast("Father's name and password are required", "error");
            return;
        }
        
        setLoader(true);
        setIsSubmitted(true);
        
        const fields = { 
            fatherName, fatherOccupation, fatherPhone, fatherEmail,
            motherName, motherOccupation, motherPhone, motherEmail,
            guardianName, guardianRelation, guardianPhone, guardianEmail, guardianAddress,
            school, address, phone, email, password, role,
            students: selectedStudents.map(s => s._id)
        };
        
        if (photo) fields.photo = photo;
        
        dispatch(registerParent(fields, role));
    };

    useEffect(() => {
        if (isSubmitted && status === 'added') {
            dispatch(underControl());
            setIsSubmitted(false);
            showToast("Parent account created successfully!", "success");
            navigate("/Admin/parents");
        }
        else if (status === 'failed') {
            showToast(response || "Failed to add parent", "error");
            setLoader(false);
            setIsSubmitted(false);
        }
    }, [status, isSubmitted, navigate, response, dispatch]);

    return (
        <PageContainer>
            <StickyHeader elevation={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 2 }}>
                    <IconButton 
                        onClick={() => navigate("/Admin/parents")}
                        sx={{ color: 'white', mr: 2, bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                            Register New Parent
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                            Select class and student first, then fill parent details
                        </Typography>
                    </Box>
                </Box>
            </StickyHeader>

            <MainContent maxWidth="md">
                <Box component="form" onSubmit={submitHandler} sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    
                    {/* Section 1: Student Connection */}
                    <FormCard elevation={2}>
                        <CardHeader color="#667eea">
                            <Typography variant="h6" color="white">Step 1: Student Selection</Typography>
                            <SchoolIcon />
                        </CardHeader>
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                        First, choose the Class:
                                    </Typography>
                                    <Autocomplete
                                        options={sclassesList || []}
                                        getOptionLabel={(option) => option.sclassName || ""}
                                        value={selectedSclass}
                                        onChange={(e, val) => setSelectedSclass(val)}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Select Class" variant="outlined" fullWidth />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                        Then, select Student(s):
                                    </Typography>
                                    <Autocomplete
                                        multiple
                                        disabled={!selectedSclass}
                                        options={availableStudents}
                                        getOptionLabel={(option) => `${option.name} (Roll: ${option.rollNum})`}
                                        value={selectedStudents}
                                        onChange={(e, val) => setSelectedStudents(val)}
                                        isOptionEqualToValue={(opt, val) => opt._id === val._id}
                                        loading={studentsLoading}
                                        renderInput={(params) => (
                                            <TextField 
                                                {...params} 
                                                label={!selectedSclass ? "Choose a class first" : "Select Student(s)"} 
                                                placeholder="Search student..." 
                                                variant="outlined" 
                                            />
                                        )}
                                        renderTags={(value, getProps) =>
                                            value.map((opt, idx) => (
                                                <Chip key={opt._id} label={opt.name} size="small" {...getProps({ index: idx })} />
                                            ))
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </FormCard>

                    {/* Section 2: Parent Information */}
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <FormCard elevation={2}>
                                <CardHeader color="#11998e">
                                    <Typography variant="h6" color="white">Father's Details</Typography>
                                    <PersonIcon />
                                </CardHeader>
                                <CardContent>
                                    <TextField fullWidth label="Father's Name *" value={fatherName} onChange={e => setFatherName(e.target.value)} required sx={{ mb: 2 }} />
                                    <TextField fullWidth label="Occupation" value={fatherOccupation} onChange={e => setFatherOccupation(e.target.value)} sx={{ mb: 2 }} />
                                    <TextField fullWidth label="Phone" value={fatherPhone} onChange={e => setFatherPhone(e.target.value)} sx={{ mb: 2 }} />
                                    <TextField fullWidth label="Email" type="email" value={fatherEmail} onChange={e => setFatherEmail(e.target.value)} />
                                </CardContent>
                            </FormCard>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormCard elevation={2}>
                                <CardHeader color="#f093fb">
                                    <Typography variant="h6" color="white">Mother's Details</Typography>
                                    <PersonIcon />
                                </CardHeader>
                                <CardContent>
                                    <TextField fullWidth label="Mother's Name" value={motherName} onChange={e => setMotherName(e.target.value)} sx={{ mb: 2 }} />
                                    <TextField fullWidth label="Occupation" value={motherOccupation} onChange={e => setMotherOccupation(e.target.value)} sx={{ mb: 2 }} />
                                    <TextField fullWidth label="Phone" value={motherPhone} onChange={e => setMotherPhone(e.target.value)} sx={{ mb: 2 }} />
                                    <TextField fullWidth label="Email" type="email" value={motherEmail} onChange={e => setMotherEmail(e.target.value)} />
                                </CardContent>
                            </FormCard>
                        </Grid>
                    </Grid>

                    {/* Section 3: Guardian & Contact */}
                    <FormCard elevation={2}>
                        <CardHeader color="#f5576c">
                            <Typography variant="h6" color="white">Guardian & Account Access</Typography>
                            <LocationOnIcon />
                        </CardHeader>
                        <CardContent>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Guardian Info (Optional)</Typography>
                                    <TextField fullWidth label="Guardian Name" value={guardianName} onChange={e => setGuardianName(e.target.value)} sx={{ mb: 2 }} />
                                    <TextField fullWidth label="Relation" value={guardianRelation} onChange={e => setGuardianRelation(e.target.value)} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Account Access</Typography>
                                    <TextField fullWidth label="Primary Email" type="email" value={email} onChange={e => setEmail(e.target.value)} sx={{ mb: 2 }} />
                                    <TextField fullWidth label="Account Password *" type="password" value={password} onChange={e => setPassword(e.target.value)} required helperText="Used for parent login portal" />
                                </Grid>
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                    <TextField fullWidth label="Primary Address" multiline rows={2} value={address} onChange={e => setAddress(e.target.value)} sx={{ mt: 2 }} />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </FormCard>

                    {/* Section 4: Photo Upload */}
                    <FormCard elevation={2}>
                        <CardHeader color="#a78bfa">
                            <Typography variant="h6" color="white">Profile Photo</Typography>
                            <CloudUploadIcon />
                        </CardHeader>
                        <CardContent>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
                                {!photoPreview ? (
                                    <UploadZone onClick={() => document.getElementById('parent-photo').click()}>
                                        <CloudUploadIcon sx={{ fontSize: 48, color: '#667eea', mb: 1 }} />
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>Click to Upload</Typography>
                                        <Typography variant="caption" color="textSecondary">PNG, JPG up to 5MB</Typography>
                                    </UploadZone>
                                ) : (
                                    <Box sx={{ position: 'relative' }}>
                                        <PreviewAvatar src={photoPreview} />
                                        <IconButton 
                                            onClick={removePhoto}
                                            sx={{ position: 'absolute', top: 0, right: 0, bgcolor: '#f44336', color: 'white', '&:hover': { bgcolor: '#d32f2f' } }}
                                            size="small"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                )}
                                <input id="parent-photo" type="file" accept="image/*" onChange={handlePhotoChange} style={{ display: 'none' }} />
                            </Box>
                        </CardContent>
                    </FormCard>

                    {/* Submit Area */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', pb: 4 }}>
                        <SubmitButton
                            type="submit"
                            variant="contained"
                            disabled={loader}
                            startIcon={loader ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                        >
                            {loader ? 'Registering...' : 'Register Parent Account'}
                        </SubmitButton>
                    </Box>

                </Box>
            </MainContent>
        </PageContainer>
    );
};

export default AddParent;

// Styled Components
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageContainer = styled.div`
    background-color: #f8fafc;
    min-height: 100vh;
`;

const StickyHeader = styled(Paper)`
    position: sticky;
    top: 0;
    z-index: 1000;
    border-radius: 0 !important;
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%) !important;
    color: white !important;
`;

const MainContent = styled(Container)`
    padding-top: 40px;
    animation: ${fadeIn} 0.5s ease-out;
`;

const FormCard = styled(Paper)`
    border-radius: 24px !important;
    overflow: hidden;
    border: 1px solid #edf2f7;
`;

const CardHeader = styled(Box)`
    padding: 16px 24px;
    background: ${props => props.color || '#667eea'};
    color: white;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const CardContent = styled(Box)`
    padding: 24px;
`;

const UploadZone = styled(Box)`
    width: 100%;
    max-width: 400px;
    height: 160px;
    border: 2px dashed #667eea;
    border-radius: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    background: #f0f4ff;
    
    &:hover {
        background: #eef2ff;
        border-color: #764ba2;
    }
`;

const PreviewAvatar = styled(Avatar)`
    width: 140px !important;
    height: 140px !important;
    border-radius: 20px !important;
    border: 4px solid #667eea;
    box-shadow: 0 8px 25px rgba(0,0,0,0.1);
`;

const SubmitButton = styled(Button)`
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
    color: white !important;
    padding: 14px 40px !important;
    border-radius: 16px !important;
    font-weight: 700 !important;
    font-size: 1.1rem !important;
    text-transform: none !important;
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4) !important;
    transition: all 0.3s ease !important;
    
    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5) !important;
    }
`;
